import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { investorApi, countryApi } from '../../services/adminApi';
import '../../styles/AdminPages.css';

const InvestorEditPage = () => {
  const navigate = useNavigate();
  const { investorId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [countries, setCountries] = useState([]);
  const [investor, setInvestor] = useState(null);
  const [formData, setFormData] = useState({});
  const [sponsorSearch, setSponsorSearch] = useState('');
  const [sponsorResults, setSponsorResults] = useState([]);
  const [sponsorLoading, setSponsorLoading] = useState(false);
  const [showSponsorDropdown, setShowSponsorDropdown] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState(null);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await countryApi.getAll({ pageSize: 300 });
      const items = response?.data?.[0] || response?.data || [];
      setCountries(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error('Error fetching countries:', err);
    }
  }, []);

  const fetchInvestor = useCallback(async () => {
    try {
      setLoading(true);
      const response = await investorApi.getById(investorId);
      const data = response?.data?.[0] || response?.data || response;
      
      if (!data || !data.investorId) {
        setError('Investor not found');
        return;
      }
      
      setInvestor(data);
      setFormData({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        phone: data.phone || '',
        gender: data.gender || '',
        birthdayDate: data.birthdayDate ? data.birthdayDate.slice(0, 10) : '',
        birthCountry: data.birthCountry || '',
        departmentOfBirth: data.departmentOfBirth || '',
        nationality: data.nationality || '',
        iban: data.iban || '',
        bic: data.bic || '',
        identityValidated: data.identityValidated || false,
        emailValidated: data.emailValidated || false,
        phoneValidated: data.phoneValidated || false,
        suspended: data.suspended || false,
        sponsorId: data.sponsorId || '',
        ownerOf: data.ownerOf || '',
        amountSource: data.amountSource || '',
        savingsCapacity: data.savingsCapacity || '',
        mainInvestment: data.mainInvestment || ''
      });
      setError('');
    } catch (err) {
      console.error('Error fetching investor:', err);
      setError(`Failed to load investor: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [investorId]);

  useEffect(() => {
    fetchCountries();
    fetchInvestor();
  }, [fetchCountries, fetchInvestor]);

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const searchSponsors = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSponsorResults([]);
      return;
    }
    
    setSponsorLoading(true);
    try {
      const response = await investorApi.getAll({ 
        search: searchTerm, 
        pageSize: 10,
        page: 1 
      });
      const data = response?.data?.[0]?.data || response?.data?.[0] || [];
      const investors = Array.isArray(data) ? data.filter(inv => inv.investorId !== investorId) : [];
      setSponsorResults(investors);
    } catch (err) {
      console.error('Error searching sponsors:', err);
      setSponsorResults([]);
    } finally {
      setSponsorLoading(false);
    }
  }, [investorId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (sponsorSearch) {
        searchSponsors(sponsorSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [sponsorSearch, searchSponsors]);

  const handleSponsorSelect = (sponsor) => {
    setSelectedSponsor(sponsor);
    handleInputChange('sponsorId', sponsor.investorId);
    setSponsorSearch('');
    setShowSponsorDropdown(false);
    setSponsorResults([]);
  };

  const clearSponsor = () => {
    setSelectedSponsor(null);
    handleInputChange('sponsorId', null);
    setSponsorSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const cleanedData = {};
    Object.keys(formData).forEach(key => {
      let value = formData[key];
      
      if (value === '' || value === undefined) {
        cleanedData[key] = null;
      } else if (key === 'birthdayDate' && value) {
        cleanedData[key] = new Date(value).toISOString();
      } else if (['birthCountry', 'nationality'].includes(key) && value) {
        cleanedData[key] = isNaN(Number(value)) ? value : Number(value);
      } else {
        cleanedData[key] = value;
      }
    });

    try {
      await investorApi.update(investorId, cleanedData);
      Swal.fire({
        title: 'Updated!',
        text: 'Investor has been updated successfully.',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
      navigate('/admin/investor');
    } catch (err) {
      console.error('Error updating investor:', err);
      setError(err.message || 'Failed to update investor');
      setSaving(false);
    }
  };

  const countryOptions = countries.map(c => ({ 
    value: c.id, 
    label: c.name || `Country ${c.id}`
  }));

  const genderOptions = [
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
    { value: 'O', label: 'Other' }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getCountryName = (id) => {
    const country = countries.find(c => c.id === id);
    return country ? country.name : '';
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading-container">
          <div className="admin-spinner"></div>
          <span>Loading investor...</span>
        </div>
      </div>
    );
  }

  if (error && !investor) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <button onClick={() => navigate('/admin/investor')} className="admin-back-btn">
            ← Back to Investors
          </button>
        </div>
        <div className="admin-form-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button 
            onClick={() => navigate('/admin/investor')} 
            className="admin-back-btn"
          >
            ← Back
          </button>
          <div>
            <h1>Edit Investor</h1>
            <p>{investor?.email}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {error && (
          <div className="admin-form-error" style={{ margin: '0 0 20px 0' }}>
            {error}
          </div>
        )}

        <div className="admin-edit-grid">
          <div className="admin-edit-card">
            <h3>Personal Information</h3>
            
            <div className="admin-form-group">
              <label>Email</label>
              <input 
                type="email" 
                value={investor?.email || ''} 
                disabled 
                className="admin-input-disabled"
              />
              <small className="admin-field-hint">Email cannot be changed from admin panel</small>
            </div>

            <div className="admin-form-row">
              <div className="admin-form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="Enter first name"
                />
              </div>
              <div className="admin-form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Enter last name"
                />
              </div>
            </div>

            <div className="admin-form-row">
              <div className="admin-form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Enter phone number"
                />
              </div>
              <div className="admin-form-group">
                <label>Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                >
                  <option value="">Select gender</option>
                  {genderOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="admin-form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                value={formData.birthdayDate}
                onChange={(e) => handleInputChange('birthdayDate', e.target.value)}
              />
            </div>
          </div>

          <div className="admin-edit-card">
            <h3>Location & Nationality</h3>
            
            <div className="admin-form-group">
              <label>Birth Country</label>
              <select
                value={formData.birthCountry}
                onChange={(e) => handleInputChange('birthCountry', e.target.value)}
              >
                <option value="">Select country</option>
                {countryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="admin-form-group">
              <label>Department of Birth</label>
              <input
                type="text"
                value={formData.departmentOfBirth}
                onChange={(e) => handleInputChange('departmentOfBirth', e.target.value)}
                placeholder="Enter department/region of birth"
              />
            </div>

            <div className="admin-form-group">
              <label>Nationality</label>
              <select
                value={formData.nationality}
                onChange={(e) => handleInputChange('nationality', e.target.value)}
              >
                <option value="">Select nationality</option>
                {countryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="admin-edit-card">
            <h3>Banking Information</h3>
            
            <div className="admin-form-group">
              <label>IBAN</label>
              <input
                type="text"
                value={formData.iban}
                onChange={(e) => handleInputChange('iban', e.target.value)}
                placeholder="Enter IBAN"
              />
            </div>

            <div className="admin-form-group">
              <label>BIC/SWIFT</label>
              <input
                type="text"
                value={formData.bic}
                onChange={(e) => handleInputChange('bic', e.target.value)}
                placeholder="Enter BIC/SWIFT code"
              />
            </div>
          </div>

          <div className="admin-edit-card">
            <h3>Verification Status</h3>
            
            <div className="admin-toggle-group">
              <label>Identity Validated</label>
              <div className="admin-toggle-btns">
                <button
                  type="button"
                  className={`admin-toggle-btn ${formData.identityValidated === true ? 'active yes' : ''}`}
                  onClick={() => handleInputChange('identityValidated', true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`admin-toggle-btn ${formData.identityValidated === false ? 'active no' : ''}`}
                  onClick={() => handleInputChange('identityValidated', false)}
                >
                  No
                </button>
              </div>
            </div>

            <div className="admin-toggle-group">
              <label>Email Validated</label>
              <div className="admin-toggle-btns">
                <button
                  type="button"
                  className={`admin-toggle-btn ${formData.emailValidated === true ? 'active yes' : ''}`}
                  onClick={() => handleInputChange('emailValidated', true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`admin-toggle-btn ${formData.emailValidated === false ? 'active no' : ''}`}
                  onClick={() => handleInputChange('emailValidated', false)}
                >
                  No
                </button>
              </div>
            </div>

            <div className="admin-toggle-group">
              <label>Phone Validated</label>
              <div className="admin-toggle-btns">
                <button
                  type="button"
                  className={`admin-toggle-btn ${formData.phoneValidated === true ? 'active yes' : ''}`}
                  onClick={() => handleInputChange('phoneValidated', true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`admin-toggle-btn ${formData.phoneValidated === false ? 'active no' : ''}`}
                  onClick={() => handleInputChange('phoneValidated', false)}
                >
                  No
                </button>
              </div>
            </div>
          </div>

          <div className="admin-edit-card">
            <h3>Account Status</h3>
            
            <div className="admin-toggle-group">
              <label>Account Suspended</label>
              <div className="admin-toggle-btns">
                <button
                  type="button"
                  className={`admin-toggle-btn ${formData.suspended === true ? 'active danger' : ''}`}
                  onClick={() => handleInputChange('suspended', true)}
                >
                  Suspended
                </button>
                <button
                  type="button"
                  className={`admin-toggle-btn ${formData.suspended === false ? 'active yes' : ''}`}
                  onClick={() => handleInputChange('suspended', false)}
                >
                  Active
                </button>
              </div>
            </div>

            <div className="admin-form-group">
              <label>Sponsor</label>
              <div className="sponsor-search-container">
                {selectedSponsor || formData.sponsorId ? (
                  <div className="selected-sponsor">
                    <span className="sponsor-info">
                      {selectedSponsor 
                        ? `${selectedSponsor.firstName || ''} ${selectedSponsor.lastName || ''} (${selectedSponsor.email})`
                        : formData.sponsorId
                      }
                    </span>
                    <button 
                      type="button" 
                      className="clear-sponsor-btn"
                      onClick={clearSponsor}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="sponsor-search-wrapper">
                    <input
                      type="text"
                      value={sponsorSearch}
                      onChange={(e) => {
                        setSponsorSearch(e.target.value);
                        setShowSponsorDropdown(true);
                      }}
                      onFocus={() => setShowSponsorDropdown(true)}
                      placeholder="Search by email, first name, or last name..."
                    />
                    {sponsorLoading && <span className="sponsor-loading">Searching...</span>}
                    {showSponsorDropdown && sponsorResults.length > 0 && (
                      <div className="sponsor-dropdown">
                        {sponsorResults.map(inv => (
                          <div
                            key={inv.investorId}
                            className="sponsor-option"
                            onClick={() => handleSponsorSelect(inv)}
                          >
                            <span className="sponsor-name">{inv.firstName} {inv.lastName}</span>
                            <span className="sponsor-email">{inv.email}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="admin-edit-card">
            <h3>Investment Profile</h3>
            
            <div className="admin-form-group">
              <label>Owner Of</label>
              <input
                type="text"
                value={formData.ownerOf}
                onChange={(e) => handleInputChange('ownerOf', e.target.value)}
                placeholder="Ownership details"
              />
            </div>

            <div className="admin-form-group">
              <label>Amount Source</label>
              <input
                type="text"
                value={formData.amountSource}
                onChange={(e) => handleInputChange('amountSource', e.target.value)}
                placeholder="Source of funds"
              />
            </div>

            <div className="admin-form-group">
              <label>Savings Capacity</label>
              <input
                type="text"
                value={formData.savingsCapacity}
                onChange={(e) => handleInputChange('savingsCapacity', e.target.value)}
                placeholder="Savings capacity info"
              />
            </div>

            <div className="admin-form-group">
              <label>Main Investment</label>
              <input
                type="text"
                value={formData.mainInvestment}
                onChange={(e) => handleInputChange('mainInvestment', e.target.value)}
                placeholder="Main investment preference"
              />
            </div>
          </div>

          <div className="admin-edit-card admin-metadata-card">
            <h3>Account Metadata</h3>
            <div className="admin-metadata-grid">
              <div className="admin-metadata-item">
                <span className="admin-metadata-label">Investor ID</span>
                <span className="admin-metadata-value">{investor?.investorId}</span>
              </div>
              <div className="admin-metadata-item">
                <span className="admin-metadata-label">Created At</span>
                <span className="admin-metadata-value">{formatDate(investor?.createdAt)}</span>
              </div>
              <div className="admin-metadata-item">
                <span className="admin-metadata-label">Last Updated</span>
                <span className="admin-metadata-value">{formatDate(investor?.updatedAt) || 'Never'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="admin-edit-actions">
          <button 
            type="button" 
            className="admin-btn secondary"
            onClick={() => navigate('/admin/investor')}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="admin-btn primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvestorEditPage;
